import { SavedViewsService } from 'src/app/shared/saved-views/saved-views.service';
import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, Subscription } from 'rxjs';
import { ApiError } from 'src/app/core/providers/error-handling/error.models';
import {
  ProfileListResponse,
  Profile,
} from 'src/app/core/providers/profile/profile.models';
import {
  UsersFirmListResponse,
  UserFirm,
} from 'src/app/core/providers/user-firm/user-firm.models';
import { ToastService } from 'src/app/core/providers/toast/toast.service';
import { AuthenticationService } from 'src/app/core/providers/auth/auth.service';
import { LoginApiResponse } from 'src/app/core/providers/auth/auth.models';
import { User } from 'src/app/core/providers/user/users.models';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { AppContextService } from 'src/app/core/providers/context/AppContextService ';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-switch-user-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
  templateUrl: './switch-user-modal.component.html',
  styleUrl: './switch-user-modal.component.scss',
})
export class SwitchUserModalComponent implements OnInit, OnDestroy {
  userFirms: UserFirm[] = [];
  profiles: Profile[] = [];

  searchForm: FormGroup;
  subscription: Subscription = new Subscription();

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private router: Router,
    private savedViews: SavedViewsService,
    private apiService: ApiService,
    private authenticationService: AuthenticationService<
      LoginApiResponse | ApiError
    >,
    private fb: FormBuilder,
    private appContextService: AppContextService
  ) {
    this.searchForm = this.fb.group({
      search: [''],
      profile: [''],
      firm: [''],
    });

    this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => this.onSearch());
  }

  ngOnInit(): void {
    this.loadUserFirms({});
    this.loadProfiles();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  close() {
    this.authenticationService.logout();
    this.activeModal.dismiss();
  }

  onSearch(): void {
    const filters: any = {};

    const search = this.searchForm.get('search')?.value?.trim();
    const profile = this.searchForm.get('profile')?.value;
    const firm = this.searchForm.get('firm')?.value;

    if (search) filters['search'] = search;
    if (profile) filters.profile = profile;
    if (firm) filters.firm = firm;

    this.loadUserFirms(filters);
  }

  loadUserFirms(filters: any) {
    const page: number = 1;
    const itemsPerPage: number = 20;
    const mergedFilters = {
      'userInfo.isEnabled': true,
      ...filters,
    };
    this.subscription.add(
      this.apiService
        .getAll('user_firms', page, itemsPerPage, mergedFilters)
        .subscribe({
          next: (response: UsersFirmListResponse) => {
            this.userFirms = response.member;
          },
        })
    );
  }

  loadProfiles() {
    const page: number = 1;
    const itemsPerPage: number = 20;
    this.subscription.add(
      this.apiService
        .getAll('profiles', page, itemsPerPage, { scope: 'site' })
        .subscribe({
          next: (response: ProfileListResponse) => {
            this.profiles = response.member;
          },
        })
    );
  }

  login(userFirm: UserFirm) {
    const userId = userFirm.userId
      ?? (typeof userFirm.user === 'string'
        ? userFirm.user.replace('/api/users/', '')
        : userFirm.user['@id']?.replace('/api/users/', ''));

    const firmId = userFirm.firmId
      ?? (typeof userFirm.firm === 'string'
        ? userFirm.firm.replace('/api/firms/', '')
        : (userFirm.firm as any)['@id']?.replace('/api/firms/', ''));

    const uri = `user-switch/${userId}/${firmId}`;
    this.subscription.add(
      this.authenticationService.loginBySwitch(uri).subscribe({
        next: (response: LoginApiResponse | ApiError) => {
          if (this.authenticationService.isLoginApiResponse(response)) {
            this.handleLoginSuccess(response);
          } else {
            this.handleError(response);
          }
        },
      })
    );
  }

  handleLoginSuccess(token: LoginApiResponse): void {
    this.fecthCurrentUserInfo();
    this.fetchFirmContext();
  }

  fecthCurrentUserInfo() {
    this.subscription.add(
      this.authenticationService.currentUserInfo().subscribe({
        next: (response: User | ApiError) => {
          if (this.authenticationService.isUser(response)) {
            this.handleCurrentUserInfoSuccess(response);
          } else {
            this.handleError(response);
          }
        },
        error: (error: ApiError) => this.handleError(error),
      })
    );
  }

  fetchFirmContext() {
    this.appContextService.loadContext().subscribe();
  }

  handleCurrentUserInfoSuccess(response: User) {
    if (this.authenticationService.currentUserValue) {
      this.activeModal.dismiss();
      this.savedViews.openHome();
    }
  }

  handleError(error: ApiError): void {
    this.toastService.show({
      message:
        'Une erreur est survenue: ' + (error.message || 'Erreur inconnue'),
      classname: 'bg-danger text-light',
      delay: 15000,
    });
  }

  get formControls() {
    return this.searchForm.controls;
  }
}
